import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const ImageComposition = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasFeatureAccess, getPlanInfo, getRequiredPlanForFeature } = usePermission();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  const [baseImage, setBaseImage] = useState(null); // Main image (e.g., person)
  const [overlayImage, setOverlayImage] = useState(null); // Secondary image (e.g., clothing)
  const [compositionPrompt, setCompositionPrompt] = useState('');
  const [compositionStyle, setCompositionStyle] = useState('natural');
  const [generating, setGenerating] = useState(false);
  const [optimizingPrompt, setOptimizingPrompt] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState('');
  const baseImageInputRef = useRef(null);
  const overlayImageInputRef = useRef(null);

  const navigationWarning = useNavigationWarning(generating || optimizingPrompt, 'An image is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'image-generation');

  // Load prefill data from Workspace if available
  useEffect(() => {
    if (location.state?.prefillData) {
      const { prompt, style, previousImage } = location.state.prefillData;

      if (prompt) {
        setCompositionPrompt(prompt);
      }

      if (style) {
        setCompositionStyle(style);
      }

      if (previousImage) {
        setGeneratedImage({
          imageUrl: previousImage,
          prompt: prompt || '',
          style: style || 'natural'
        });
      }

      // Clear the navigation state to prevent re-filling on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleImageUpload = (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPEG, PNG, and WEBP images are allowed');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const imageData = {
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    };

    if (type === 'base') {
      // Revoke previous URL if exists
      if (baseImage) {
        URL.revokeObjectURL(baseImage.preview);
      }
      setBaseImage(imageData);
    } else {
      // Revoke previous URL if exists
      if (overlayImage) {
        URL.revokeObjectURL(overlayImage.preview);
      }
      setOverlayImage(imageData);
    }
  };

  const removeImage = (type) => {
    if (type === 'base' && baseImage) {
      URL.revokeObjectURL(baseImage.preview);
      setBaseImage(null);
    } else if (type === 'overlay' && overlayImage) {
      URL.revokeObjectURL(overlayImage.preview);
      setOverlayImage(null);
    }
  };

  const handleOptimizePrompt = async () => {
    if (!compositionPrompt.trim()) {
      setError('Please enter a composition description first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setOptimizingPrompt(true);
    setError('');

    try {
      const response = await contentAPI.optimizeImagePrompt({
        prompt: compositionPrompt.trim(),
        style: compositionStyle
      });

      if (response.data.success) {
        setCompositionPrompt(response.data.data.optimizedPrompt);
      } else {
        setError(response.data.error?.message || 'Failed to optimize prompt');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      console.error('Prompt optimization error:', err);
      setError(err.response?.data?.error?.message || 'Failed to optimize prompt. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setOptimizingPrompt(false);
    }
  };

  const handleGenerate = async () => {
    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    if (!baseImage || !overlayImage) {
      setError('Please upload both base image and overlay image');
      setTimeout(() => setError(''), 3000);
      return;
    }

    if (!compositionPrompt.trim()) {
      setError('Please provide a composition prompt');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('image-composition')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'Image Composition',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('image-composition')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    setError('');
    setGeneratedImage(null);

    try {
      const formData = new FormData();
      formData.append('images', baseImage.file);
      formData.append('images', overlayImage.file);
      formData.append('prompt', compositionPrompt);
      formData.append('style', compositionStyle);

      const response = await contentAPI.composeImages(formData);

      if (response.data.success) {
        setGeneratedImage(response.data.data);
      } else {
        setError(response.data.error?.message || 'Failed to compose images');
      }
    } catch (err) {
      console.error('Image composition error:', err);
      setError(err.response?.data?.error?.message || 'Failed to compose images');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage?.imageUrl) {
      const link = document.createElement('a');
      link.href = generatedImage.imageUrl;
      link.download = 'composed-image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const clearAll = () => {
    if (baseImage) {
      URL.revokeObjectURL(baseImage.preview);
      setBaseImage(null);
    }
    if (overlayImage) {
      URL.revokeObjectURL(overlayImage.preview);
      setOverlayImage(null);
    }
    setGeneratedImage(null);
    setCompositionPrompt('');
    setError('');
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-2">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    Image Composition
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Upload multiple images and compose them with AI
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Panel - Upload & Settings */}
              <div className="space-y-6">
                {/* Upload Area - Two Separate Upload Boxes */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Upload Images
                  </h2>

                  {/* Two Image Upload Boxes with Plus Icon */}
                  <div className="flex items-center gap-4">
                    {/* Base Image Upload */}
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Base Image (e.g., Person)
                      </label>
                      {!baseImage ? (
                        <div
                          onClick={() => baseImageInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors h-48 flex flex-col items-center justify-center"
                        >
                          <svg className="mx-auto h-10 w-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-xs text-gray-600 font-medium">Click to upload</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP</p>
                        </div>
                      ) : (
                        <div className="relative group h-48">
                          <img
                            src={baseImage.preview}
                            alt="Base image"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removeImage('base')}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded truncate">
                            {baseImage.name}
                          </div>
                        </div>
                      )}
                      <input
                        ref={baseImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload('base', e)}
                        className="hidden"
                      />
                    </div>

                    {/* Plus Icon */}
                    <div className="flex-shrink-0 self-center mt-6">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </div>

                    {/* Overlay Image Upload */}
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Overlay Image (e.g., Clothing)
                      </label>
                      {!overlayImage ? (
                        <div
                          onClick={() => overlayImageInputRef.current?.click()}
                          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors h-48 flex flex-col items-center justify-center"
                        >
                          <svg className="mx-auto h-10 w-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <p className="text-xs text-gray-600 font-medium">Click to upload</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP</p>
                        </div>
                      ) : (
                        <div className="relative group h-48">
                          <img
                            src={overlayImage.preview}
                            alt="Overlay image"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            onClick={() => removeImage('overlay')}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded truncate">
                            {overlayImage.name}
                          </div>
                        </div>
                      )}
                      <input
                        ref={overlayImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload('overlay', e)}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {/* Example Hint */}
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-purple-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-purple-900">Usage Example</p>
                        <p className="text-xs text-purple-800 mt-1">
                          Base Image: Person photo | Overlay Image: Clothing item → Result: Person wearing new outfit
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Composition Settings */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Composition Settings
                  </h2>

                  {/* Prompt */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Composition Description
                      </label>
                      {compositionPrompt.trim() && (
                        <button
                          onClick={handleOptimizePrompt}
                          disabled={optimizingPrompt}
                          className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-600 hover:to-pink-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {optimizingPrompt ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Optimizing...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span>AI Optimize</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <textarea
                      value={compositionPrompt}
                      onChange={(e) => setCompositionPrompt(e.target.value)}
                      rows="4"
                      placeholder="Describe how you want to combine these images... (e.g., 'Make the person wear this outfit naturally' or 'Seamlessly blend the clothing onto the person')"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Style Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Composition Style
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { value: 'natural', label: 'Natural', desc: 'Seamless blend' },
                        { value: 'collage', label: 'Collage', desc: 'Grid layout' },
                        { value: 'artistic', label: 'Artistic', desc: 'Creative merge' },
                        { value: 'panorama', label: 'Panorama', desc: 'Wide view' }
                      ].map((style) => (
                        <button
                          key={style.value}
                          onClick={() => setCompositionStyle(style.value)}
                          className={`p-3 border-2 rounded-lg text-left transition-all ${
                            compositionStyle === style.value
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium text-sm text-gray-900">{style.label}</div>
                          <div className="text-xs text-gray-500">{style.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={handleGenerate}
                      disabled={generating || !baseImage || !overlayImage || !compositionPrompt.trim()}
                      className={`flex-1 py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                        generating || !baseImage || !overlayImage || !compositionPrompt.trim()
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 hover:bg-purple-700'
                      }`}
                    >
                      {generating ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Composing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Generate Composition
                        </span>
                      )}
                    </button>

                    <button
                      onClick={clearAll}
                      disabled={generating || optimizingPrompt}
                      className={`px-4 py-3 border rounded-lg font-medium transition-colors ${
                        generating || optimizingPrompt
                          ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Panel - Result */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Composed Result
                  </h2>

                  {/* Error Message */}
                  {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Result Display */}
                  <div className="bg-gray-50 rounded-lg p-8 min-h-[400px] flex items-center justify-center">
                    {generatedImage ? (
                      <div className="w-full">
                        <img
                          src={generatedImage.imageUrl}
                          alt="Composed result"
                          className="w-full rounded-lg shadow-lg"
                        />

                        {/* Download Button */}
                        <button
                          onClick={handleDownload}
                          className="mt-4 w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download Image
                        </button>

                        {/* Metadata */}
                        {generatedImage.metadata && (
                          <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
                            <h3 className="text-sm font-medium text-gray-900 mb-2">Generation Details</h3>
                            <div className="space-y-1 text-xs text-gray-600">
                              {generatedImage.metadata.model && (
                                <div>Model: {generatedImage.metadata.model}</div>
                              )}
                              {generatedImage.metadata.dimensions && (
                                <div>Dimensions: {generatedImage.metadata.dimensions}</div>
                              )}
                              {generatedImage.metadata.processingTime && (
                                <div>Processing Time: {generatedImage.metadata.processingTime}s</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-gray-500 text-sm">
                          Your composed image will appear here
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="h-5 w-5 text-blue-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-900">Tips for Best Results</h3>
                      <div className="mt-2 text-sm text-blue-800">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Use high-quality images with similar lighting</li>
                          <li>Base image should have clear subject (e.g., person)</li>
                          <li>Overlay image should be relevant to base (e.g., clothing)</li>
                          <li>Describe clearly how to combine them</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ImageComposition;
